import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLoaderData } from 'react-router-dom';
import { VaBreadcrumbs } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { useFeatureToggle } from '~/platform/utilities/feature-toggles/useFeatureToggle';
import CombinedHistory from '../components/CombinedHistory';
import SubmissionsWrapper from '../components/SubmissionsWrapper';
import api from '../utilities/api';
import { loadCombinedHistory } from '../utilities/combinedHistory';
import { submissionHistoryBC } from '../utilities/poaRequests';
import store from '../utilities/store';
import { useHistoryReload } from '../utilities/useHistoryReload';
import { SUBMISSIONS_PATH, SUBMIT_FORMS_PATH } from '../utilities/constants';

/**
 * The portal's full submission history.
 *
 * @param {{ title: string }} props
 * @param {string} props.title The document title.
 * @returns {JSX.Element}
 */
const SubmissionHistoryPage = ({ title }) => {
  const { TOGGLE_NAMES, useToggleValue } = useFeatureToggle();
  const isSubmissionsUpdateEnabled = useToggleValue(
    TOGGLE_NAMES.accreditedRepresentativePortalCdUpdateMain,
  );

  const { combinedHistoryDisabled, combinedHistory, abortCombinedHistory } =
    useLoaderData();
  const { history, isLoading, onRefresh } = useHistoryReload(
    combinedHistory,
    null,
    abortCombinedHistory,
  );

  useEffect(() => {
    document.title = title;
  }, [title]);

  return (
    <section
      className={`vads-u-width--full claimant-details-submission-history${
        combinedHistoryDisabled ? '' : ' submission-history-page'
      }`}
    >
      <div className="claimant-details-submission-history__breadcrumbs">
        <VaBreadcrumbs
          breadcrumbList={submissionHistoryBC}
          homeVeteransAffairs={false}
          wrapping
        />
      </div>

      <SubmissionsWrapper>
        <h2
          data-testid="submission-history-page-heading"
          className="vads-u-margin-y--0"
        >
          Submission history
        </h2>
        <p className="va-introtext vads-u-margin-y--0 vads-u-margin-bottom--2">
          Review and monitor your submissions sent through the portal over the
          past 60 days.
        </p>
        {combinedHistoryDisabled ? (
          <>
            <p>This page isn’t available yet.</p>
            <va-link-action
              href={
                isSubmissionsUpdateEnabled
                  ? SUBMIT_FORMS_PATH
                  : SUBMISSIONS_PATH
              }
              text="Go to Submissions page"
              type="primary"
            />
          </>
        ) : (
          <CombinedHistory
            submissions={history?.submissions}
            hasError={history?.error}
            isLoading={isLoading}
            partial={history?.partial}
            onRefresh={onRefresh}
            loadStartedAt={history?.loadStartedAt}
            scope="representative"
          />
        )}
      </SubmissionsWrapper>
    </section>
  );
};

SubmissionHistoryPage.loader = loadCombinedHistory(api, store);

SubmissionHistoryPage.propTypes = {
  title: PropTypes.string.isRequired,
};

export default SubmissionHistoryPage;
